/**
 * runtimeBootstrap.ts — Runtime-neutral bootstrap resolution for component jobs.
 *
 * Exported for use by:
 * - Wasmtime run invocation payloads
 * - Guest SDK helper: the `openWorkspaceFromRunnerBootstrap()` helper in this module
 *
 * This module intentionally contains NO Node-only imports (no `node:crypto`, `node:fs`, etc.)
 * so it can be safely imported from both Node host code and guest WASM runtime code.
 */

import {
    decodeConfigConnectionTokenV2,
    decodeWorkspaceSecretV2,
} from '../workspaceSecretV2'
import type { ComponentRunnerBootstrap } from '../componentJobs'
import type { WorkspaceSession } from '../session'
import {
    DEFAULT_PAGE_SIZE,
    maxWalShadowSizeForPageSize,
} from '../sqlite3mt/sab-layout'
import type { Sqlite3MtOptions } from '../sqliteEngine'

const WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS_ENV = 'SECUREBLOB_COMPONENT_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS'
const DEFAULT_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS = 1_800_000
const MIN_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS = 100
const MAX_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS = 1_800_000

export const COMPONENT_JOB_WAL_SHADOW_SIZE = maxWalShadowSizeForPageSize(DEFAULT_PAGE_SIZE)

/**
 * Resolved bootstrap ready for SDK creation and workspace open.
 * Produced by `resolveBootstrap()` from raw runner input.
 */
export interface ResolvedBootstrap {
    apiBaseUrl: string
    workspaceBlobId: string
    workspaceAlias?: string
    workspaceSecret?: string
    configConnectionToken?: string
    localStorePolicy: 'memory' | 'indexeddb'
    secretEnv?: Record<string, string>
}

export function componentJobSqlite3MtOptions(
    localStorePolicy: ResolvedBootstrap['localStorePolicy'],
): Sqlite3MtOptions {
    return {
        backend: 'sqlite3mtlight',
        persistence: localStorePolicy === 'indexeddb' ? 'idb' : 'memory',
        initialWalSize: COMPONENT_JOB_WAL_SHADOW_SIZE,
        maxWalSize: COMPONENT_JOB_WAL_SHADOW_SIZE,
    }
}

/**
 * Resolve a raw runner bootstrap into a clean form ready for SDK use.
 *
 * Rules (§71 §7.2 contract):
 * - `apiBaseUrl` must be present and normalized (trailing slashes stripped).
 * - Either `workspaceSecret` OR `configConnectionToken` must be present.
 * - If `workspaceSecret` is a v2 secret, derive the canonical `workspaceBlobId` from it.
 * - If `configConnectionToken` is present, validate its embedded config-blob
 *   secret but do not treat that secret as the target workspace secret.
 * - `workspaceBlobId` always refers to the target workspace to open (alias/blobId),
 *   so it must come from explicit bootstrap input, fallback value, or workspaceSecret.
 * - `localStorePolicy` defaults to `'memory'` if not `'indexeddb'`.
 */
export function resolveBootstrap(
    bootstrap: ComponentRunnerBootstrap,
    fallbackWorkspaceBlobId: string,
): ResolvedBootstrap {
    const apiBaseUrl = bootstrap.apiBaseUrl.trim().replace(/\/+$/, '')
    const workspaceAlias = normalizeOptionalString(bootstrap.workspaceAlias)
    let workspaceSecret = normalizeOptionalString(bootstrap.workspaceSecret)
    const configConnectionToken = normalizeOptionalString(bootstrap.configConnectionToken)
    const localStorePolicy = bootstrap.localStorePolicy === 'indexeddb' ? 'indexeddb' : 'memory'
    const secretEnv = normalizeSecretEnv(bootstrap.secretEnv)

    if (!apiBaseUrl) {
        throw new Error('Runner bootstrap is missing apiBaseUrl.')
    }
    if (!workspaceSecret && !configConnectionToken) {
        throw new Error('Runner bootstrap requires either workspaceSecret or configConnectionToken.')
    }

    let workspaceBlobId = normalizeOptionalString(bootstrap.workspaceBlobId) || fallbackWorkspaceBlobId
    if (workspaceSecret) {
        const decoded = decodeWorkspaceSecretV2(workspaceSecret)
        if (!decoded) {
            throw new Error('Runner bootstrap workspaceSecret is not a valid workspace-secret-v2.')
        }
        workspaceBlobId = decoded.blobId
    }

    if (configConnectionToken) {
        const decodedToken = decodeConfigConnectionTokenV2(configConnectionToken)
        if (!decodedToken) {
            throw new Error('Runner bootstrap configConnectionToken is not a valid config-connection-token-v2.')
        }
        const tokenApiBaseUrl = decodedToken.apiBaseUrl.trim().replace(/\/+$/, '')
        if (tokenApiBaseUrl !== apiBaseUrl) {
            throw new Error('Runner bootstrap apiBaseUrl does not match configConnectionToken apiBaseUrl.')
        }
        const decodedTokenSecret = decodeWorkspaceSecretV2(decodedToken.secret)
        if (!decodedTokenSecret) {
            throw new Error('Runner bootstrap configConnectionToken does not embed a valid workspace-secret-v2.')
        }
    }

    if (!workspaceBlobId) {
        throw new Error('Runner bootstrap is missing workspaceBlobId and no fallback was provided.')
    }

    return {
        apiBaseUrl,
        workspaceBlobId,
        ...(workspaceAlias ? { workspaceAlias } : {}),
        ...(workspaceSecret ? { workspaceSecret } : {}),
        ...(configConnectionToken ? { configConnectionToken } : {}),
        localStorePolicy,
        ...(Object.keys(secretEnv).length > 0 ? { secretEnv } : {}),
    }
}

function normalizeOptionalString(value: string | undefined): string {
    return typeof value === 'string' ? value.trim() : ''
}

function normalizeSecretEnv(input: Record<string, string> | undefined): Record<string, string> {
    if (!input || typeof input !== 'object') {
        return {}
    }

    return Object.fromEntries(
        Object.entries(input)
            .map(([name, secretRef]) => [name.trim(), typeof secretRef === 'string' ? secretRef.trim() : ''] as const)
            .filter(([name, secretRef]) => name.length > 0 && secretRef.length > 0)
            .sort(([left], [right]) => left.localeCompare(right)),
    )
}

/**
 * Returns true when a workspace-open failure should trigger bootstrap-on-first-open.
 * Matched by `acl_not_found`, `401`, `403`, and `workspace auth` error messages.
 */
export function shouldAttemptBootstrap(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false
    }

    const message = error.message.toLowerCase()
    return message.includes('acl_not_found')
        || message.includes('401')
        || message.includes('403')
        || message.includes('workspace auth')
}

/**
 * Returns true when a bootstrap-on-first-open failure should be silently ignored.
 * Matched by `409`, `already exists`, and `already bootstrapped` messages.
 */
export function isIgnorableBootstrapError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false
    }

    const message = error.message.toLowerCase()
    return message.includes('409')
        || message.includes('already exists')
        || message.includes('already bootstrapped')
}

type SyncCapableWorkspaceSession = WorkspaceSession & {
    syncNow(): Promise<void>
    readonly syncState: WorkspaceSession['syncState']
    on: WorkspaceSession['on']
}

export interface WorkspaceHydrationOptions {
    /**
     * `sync` preserves the historical strict `syncNow()` bootstrap. `on-demand`
     * initializes the lazy remote tree with a bounded path read and avoids
     * materializing the whole workspace before component code opens sqlite3mt or
     * reads specific paths.
     */
    mode?: 'sync' | 'on-demand'
    timeoutMs?: number
    /**
     * When true, a sync timeout or sync error during hydration does NOT throw;
     * instead `hydrateWorkspaceSessionForRunner` returns `{ synced: false, timedOut: true, errorMessage }`.
     *
     * Callers that set this MUST avoid writes unless `hydration.synced` is true,
     * since the workspace may contain stale data.
     */
    allowStaleOnTimeout?: boolean
}

export interface WorkspaceHydrationResult {
    synced: boolean
    timedOut: boolean
    errorMessage: string | null
}

export async function hydrateWorkspaceSessionForRunner(
    session: WorkspaceSession,
    workspaceBlobId: string,
    options: WorkspaceHydrationOptions = {},
): Promise<WorkspaceHydrationResult> {
    if (!isSyncCapableWorkspaceSession(session)) {
        return { synced: true, timedOut: false, errorMessage: null }
    }

    const timeoutMs = normalizeWorkspaceBootstrapSyncTimeoutMs(options.timeoutMs ?? readWorkspaceBootstrapSyncTimeoutMs())
    const deadline = Date.now() + timeoutMs

    try {
        await waitForWorkspaceSyncIdle(session, workspaceBlobId, deadline, timeoutMs)
        if (options.mode === 'on-demand') {
            await hydrateWorkspaceSessionOnDemand(session, workspaceBlobId, deadline, timeoutMs)
        } else {
            await withWorkspaceSyncTimeout(
                session.syncNow(),
                workspaceBlobId,
                remainingWorkspaceSyncTimeoutMs(deadline, timeoutMs),
            )
        }
        await waitForWorkspaceSyncIdle(session, workspaceBlobId, deadline, timeoutMs)

        const lastSyncError = session.syncState.lastSyncError
        if (lastSyncError) {
            if (options.allowStaleOnTimeout) {
                return { synced: false, timedOut: false, errorMessage: lastSyncError.message }
            }
            throw new Error(
                `Workspace ${workspaceBlobId} failed to sync before component job bootstrap: ${lastSyncError.message}`,
            )
        }

        return { synced: true, timedOut: false, errorMessage: null }
    } catch (error) {
        if (options.allowStaleOnTimeout) {
            const message = error instanceof Error ? error.message : String(error)
            return { synced: false, timedOut: true, errorMessage: message }
        }
        throw error
    }
}

async function hydrateWorkspaceSessionOnDemand(
    session: SyncCapableWorkspaceSession,
    workspaceBlobId: string,
    deadline: number,
    timeoutMs: number,
): Promise<void> {
    const setSyncPolicy = (session as { setSyncPolicy?: (policy: 'on-demand') => void }).setSyncPolicy
    if (typeof setSyncPolicy === 'function') setSyncPolicy.call(session, 'on-demand')

    const readText = (session as { readText?: (path: string) => Promise<string | null> }).readText
    if (typeof readText !== 'function') {
        await withWorkspaceSyncTimeout(
            session.syncNow(),
            workspaceBlobId,
            remainingWorkspaceSyncTimeoutMs(deadline, timeoutMs),
        )
        return
    }

    await withWorkspaceSyncTimeout(
        readText.call(session, '/.secureblob/component-bootstrap-on-demand-probe'),
        workspaceBlobId,
        remainingWorkspaceSyncTimeoutMs(deadline, timeoutMs),
    )
}

function isSyncCapableWorkspaceSession(session: WorkspaceSession): session is SyncCapableWorkspaceSession {
    const candidate = session as Partial<SyncCapableWorkspaceSession>
    return typeof candidate.syncNow === 'function'
        && typeof candidate.on === 'function'
        && typeof candidate.syncState === 'object'
        && candidate.syncState !== null
}

async function waitForWorkspaceSyncIdle(
    session: SyncCapableWorkspaceSession,
    workspaceBlobId: string,
    deadline: number,
    timeoutMs: number,
): Promise<void> {
    if (!session.syncState.syncing) {
        return
    }

    await withWorkspaceSyncTimeout(new Promise<void>((resolve, reject) => {
        let unsubscribe: (() => void) | undefined
        const finish = (error?: Error) => {
            unsubscribe?.()
            if (error) {
                reject(error)
            } else {
                resolve()
            }
        }

        unsubscribe = session.on('state:changed', (state) => {
            if (!state.syncing) {
                finish()
            }
        })

        if (!session.syncState.syncing) {
            finish()
        }
    }), workspaceBlobId, remainingWorkspaceSyncTimeoutMs(deadline, timeoutMs))
}

async function withWorkspaceSyncTimeout<T>(
    promise: Promise<T>,
    workspaceBlobId: string,
    timeoutMs: number,
): Promise<T> {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined
    try {
        return await Promise.race([
            promise,
            new Promise<never>((_, reject) => {
                timeoutHandle = setTimeout(() => {
                    reject(new Error(
                        `Timed out syncing workspace ${workspaceBlobId} before component job bootstrap after ${timeoutMs}ms.`,
                    ))
                }, timeoutMs)
            }),
        ])
    } finally {
        if (timeoutHandle !== undefined) {
            clearTimeout(timeoutHandle)
        }
    }
}

function readWorkspaceBootstrapSyncTimeoutMs(): number {
    const raw = readProcessEnv(WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS_ENV)?.trim()
    if (!raw) {
        return DEFAULT_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS
    }

    const parsed = Number.parseInt(raw, 10)
    return normalizeWorkspaceBootstrapSyncTimeoutMs(Number.isFinite(parsed) ? parsed : DEFAULT_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS)
}

function readProcessEnv(name: string): string | undefined {
    const globalWithProcess = globalThis as typeof globalThis & {
        __secureblobComponentJobRuntimeEnv?: Record<string, string | undefined>
        process?: { env?: Record<string, string | undefined> }
    }
    const runtimeValue = globalWithProcess.__secureblobComponentJobRuntimeEnv?.[name]
    if (typeof runtimeValue === 'string') {
        return runtimeValue
    }
    return globalWithProcess.process?.env?.[name]
}

function remainingWorkspaceSyncTimeoutMs(deadline: number, fallbackMs: number): number {
    return Math.max(MIN_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS, deadline - Date.now() || fallbackMs)
}

function normalizeWorkspaceBootstrapSyncTimeoutMs(timeoutMs: number): number {
    if (!Number.isFinite(timeoutMs)) {
        return DEFAULT_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS
    }
    return Math.min(MAX_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS, Math.max(MIN_WORKSPACE_BOOTSTRAP_SYNC_TIMEOUT_MS, Math.trunc(timeoutMs)))
}

function isWorkspaceSyncTimeout(error: unknown): boolean {
    return error instanceof Error
        && error.message.startsWith('Timed out syncing workspace ')
        && error.message.includes(' before component job bootstrap after ')
}
